import type { Bom, Item, ProductionOrder, ProductionOrderCreate, ProductionOrderKind, StockLevel, Warehouse } from '@erp/shared';
import { ArrowLeft, Factory, Hammer, ScanLine, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Button, Combobox, type ComboboxOption, confirmDialog, FormField, Input, NumberInput,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';

/** One component row; display fields + the level (qty/avg cost) snapshot. */
interface OrderLine {
  _key: string;
  itemId: string;
  code: string;
  name: string;
  uom: string;
  /** TOTAL base units for the order (the BOM prefill scales per batch × batches). */
  quantity: string;
  /** Per OUTPUT unit, derived from the BOM — drives the max-producible banner. */
  perUnit: string | null;
}

let keySeq = 0;
const nextKey = (): string => `pl-${++keySeq}`;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 30;

export interface ProductionOrderFormPageProps {
  edit?: boolean;
}

/**
 * The "make now" screen. Pick a product → its recipe prefills the components,
 * scaled to the quantity → the grid shows what's on hand and what it costs →
 * save a draft, or post it in one go. Disassembly is the same screen with the
 * direction flipped: the product is consumed and the components come back.
 *
 * The cost card is an ESTIMATE, per currency, from the cached stock levels —
 * the server recomputes authoritatively at post (with the day's FX rate).
 */
export function ProductionOrderFormPage({ edit = false }: ProductionOrderFormPageProps): JSX.Element {
  const { t, i18n } = useTranslation('manufacturing');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const canPost = usePermission('manufacturing.post');

  const [warehouses, setWarehouses] = useState<Warehouse[] | null>(null);
  const [defaultWhId, setDefaultWhId] = useState<string | null>(null);
  const [kind, setKind] = useState<ProductionOrderKind>('assemble');
  const [product, setProduct] = useState<Item | null>(null);
  const [bom, setBom] = useState<Bom | null | 'none'>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [qty, setQty] = useState('1');
  const [costCcy, setCostCcy] = useState('');
  const [extraCost, setExtraCost] = useState('0');
  const [extraCcy, setExtraCcy] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [hydrated, setHydrated] = useState(!edit);
  const [saving, setSaving] = useState(false);

  // Product picker (server search) + component scan box — two independent boxes.
  const [productTerm, setProductTerm] = useState('');
  const [productSearch, setProductSearch] = useState<{ q: string; items: Item[] }>({ q: '', items: [] });
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState<{ q: string; items: Item[] }>({ q: '', items: [] });
  const itemsById = useRef(new Map<string, Item>());
  /** Level cache `${warehouseId}:${itemId}` → the whole level (qty + avg cost). */
  const [levels, setLevels] = useState<Record<string, { quantity: string; avgCost: string; ccy: string | null }>>({});
  const levelsRef = useRef(levels);
  levelsRef.current = levels;
  const pendingLevels = useRef(new Set<string>());
  const scanWrapRef = useRef<HTMLDivElement>(null);
  const productWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void api().get<{ items: Warehouse[]; defaultWarehouseId: string | null }>('/warehouses')
      .then((r) => {
        setWarehouses(r.data.items);
        setDefaultWhId(r.data.defaultWarehouseId);
      })
      .catch(() => setWarehouses([]));
  }, []);
  // Default the warehouse once both facts are in (and nothing chosen yet).
  useEffect(() => {
    if (!warehouseId && warehouses && warehouses.length > 0) {
      const preferred = defaultWhId && warehouses.some((w) => w.id === defaultWhId && w.isActive) ? defaultWhId : warehouses.find((w) => w.isActive)?.id;
      if (preferred) setWarehouseId(preferred);
    }
  }, [warehouses, defaultWhId, warehouseId]);

  // Edit: hydrate a draft.
  useEffect(() => {
    if (!edit || !id) return undefined;
    let on = true;
    void (async () => {
      try {
        const r = await api().get<ProductionOrder>(`/manufacturing/orders/${id}`);
        if (!on) return;
        if (r.data.status !== 'draft') {
          navigate(`/manufacturing/orders/${id}`, { replace: true });
          return;
        }
        setKind(r.data.kind);
        setWarehouseId(r.data.warehouseId);
        setQty(trimMoney(r.data.quantity));
        setCostCcy(r.data.costCurrencyCode ?? '');
        setExtraCost(trimMoney(r.data.extraCost));
        setExtraCcy(r.data.extraCostCurrencyCode ?? '');
        setNotes(r.data.notes ?? '');
        try {
          const it = (await api().get<Item>(`/items/${r.data.itemId}`)).data;
          if (on) { itemsById.current.set(it.id, it); setProduct(it); }
        } catch { /* deleted product on a draft — the picker stays empty */ }
        setLines(r.data.lines.map((l) => ({
          _key: nextKey(),
          itemId: l.itemId,
          code: l.itemCode,
          name: l.itemName,
          uom: l.itemUom,
          quantity: trimMoney(safeDecimal(l.quantity).times(safeDecimal(l.unitFactor)).toString()),
          perUnit: null,
        })));
        setHydrated(true);
      } catch {
        if (!on) return;
        toast.error(t('orders.err.loadFailed', { defaultValue: 'Could not load the order.' }));
        navigate('/manufacturing/orders', { replace: true });
      }
    })();
    return () => { on = false; };
  }, [edit, id, navigate, t]);

  // Product search (products only — a service has nothing to assemble).
  useEffect(() => {
    const q = productTerm.trim();
    if (!q) { setProductSearch({ q: '', items: [] }); return undefined; }
    let on = true;
    const handle = window.setTimeout(() => {
      void api().get<{ items: Item[] }>('/items', { params: { q, limit: SEARCH_LIMIT } })
        .then((r) => {
          if (!on) return;
          for (const it of r.data.items) itemsById.current.set(it.id, it);
          setProductSearch({ q, items: r.data.items.filter((it) => it.isActive && it.itemType === 'product') });
        })
        .catch(() => { if (on) setProductSearch({ q, items: [] }); });
    }, SEARCH_DEBOUNCE_MS);
    return () => { on = false; window.clearTimeout(handle); };
  }, [productTerm]);

  // Component search.
  useEffect(() => {
    const q = term.trim();
    if (!q) { setSearch({ q: '', items: [] }); return undefined; }
    let on = true;
    const handle = window.setTimeout(() => {
      void api().get<{ items: Item[] }>('/items', { params: { q, limit: SEARCH_LIMIT } })
        .then((r) => {
          if (!on) return;
          for (const it of r.data.items) itemsById.current.set(it.id, it);
          setSearch({ q, items: r.data.items.filter((it) => it.isActive && it.id !== product?.id) });
        })
        .catch(() => { if (on) setSearch({ q, items: [] }); });
    }, SEARCH_DEBOUNCE_MS);
    return () => { on = false; window.clearTimeout(handle); };
  }, [term, product?.id]);

  // Levels (qty + avg cost) for every grid line AND the product itself.
  useEffect(() => {
    if (!warehouseId) return undefined;
    const wanted = [...lines.map((l) => l.itemId), ...(product ? [product.id] : [])];
    const missing = wanted.filter((itemId) => {
      const key = `${warehouseId}:${itemId}`;
      return levelsRef.current[key] === undefined && !pendingLevels.current.has(key);
    });
    if (missing.length === 0) return undefined;
    const wh = warehouseId;
    for (const itemId of missing) pendingLevels.current.add(`${wh}:${itemId}`);
    void Promise.all(missing.map(async (itemId) => {
      try {
        const r = await api().get<{ items: StockLevel[] }>('/stock/levels', { params: { itemId, warehouseId: wh, limit: 1 } });
        const lv = r.data.items[0];
        return [itemId, { quantity: lv?.quantity ?? '0', avgCost: lv?.avgCost ?? '0', ccy: lv?.avgCostCurrencyCode ?? null }] as const;
      } catch {
        return [itemId, null] as const;
      }
    })).then((pairs) => {
      for (const [itemId] of pairs) pendingLevels.current.delete(`${wh}:${itemId}`);
      setLevels((prev) => {
        const next = { ...prev };
        for (const [itemId, lv] of pairs) if (lv !== null) next[`${wh}:${itemId}`] = lv;
        return next;
      });
    });
    return undefined;
  }, [warehouseId, lines, product]);

  /** Prefill the grid from the product's recipe, scaled to the current qty. */
  const applyBom = (b: Bom, outputQty: string): void => {
    const batches = safeDecimal(outputQty || '1').dividedBy(safeDecimal(b.outputQuantity));
    setLines(b.lines.map((l) => {
      const perBatchBase = safeDecimal(l.quantity).times(safeDecimal(l.unitFactor));
      const perUnit = perBatchBase.dividedBy(safeDecimal(b.outputQuantity));
      return {
        _key: nextKey(),
        itemId: l.componentItemId,
        code: l.componentCode,
        name: l.componentName,
        uom: l.componentUom,
        quantity: trimMoney(perBatchBase.times(batches).toDecimalPlaces(4).toString()),
        perUnit: perUnit.toString(),
      };
    }));
    if (safeDecimal(b.extraCost).gt(0)) {
      setExtraCost(trimMoney(b.extraCost));
      setExtraCcy(b.extraCostCurrencyCode ?? '');
    }
  };

  const pickProduct = async (value: string | undefined): Promise<void> => {
    if (!value) return;
    setProductTerm('');
    setProductSearch({ q: '', items: [] });
    const item = itemsById.current.get(value);
    if (!item) return;
    setProduct(item);
    setBom(null);
    try {
      const r = await api().get<Bom>(`/manufacturing/boms/by-item/${item.id}`);
      setBom(r.data);
      applyBom(r.data, qty);
    } catch {
      setBom('none');
      setLines([]);
    }
  };

  /** Re-scale BOM-derived lines when the output quantity changes. */
  const changeQty = (raw: string): void => {
    const clean = normalizeNumericInput(raw);
    setQty(clean);
    if (bom && bom !== 'none' && safeDecimal(clean).gt(0)) applyBom(bom, clean);
  };

  const focusScan = (): void => { scanWrapRef.current?.querySelector('input')?.focus(); };

  const addComponent = (item: Item, addBase: string): void => {
    if (product && item.id === product.id) {
      toast.error(t('boms.selfReference', { defaultValue: 'A product cannot be a component of itself.' }));
      return;
    }
    itemsById.current.set(item.id, item);
    setLines((prev) => {
      const existing = prev.find((l) => l.itemId === item.id);
      if (existing) {
        return prev.map((l) => (l.itemId === item.id
          ? { ...l, quantity: safeDecimal(l.quantity).plus(safeDecimal(addBase)).toString(), perUnit: null }
          : l));
      }
      return [...prev, { _key: nextKey(), itemId: item.id, code: item.code, name: item.name, uom: item.unitOfMeasure, quantity: trimMoney(addBase), perUnit: null }];
    });
    focusScan();
  };

  const onScanPick = (value: string | undefined): void => {
    if (!value) return;
    setTerm('');
    setSearch({ q: '', items: [] });
    const item = itemsById.current.get(value);
    if (item) addComponent(item, '1');
  };

  const resolveScan = async (code: string): Promise<void> => {
    setTerm('');
    setSearch({ q: '', items: [] });
    try {
      const { data } = await api().get<{ item: Item; itemUnitId: string | null }>('/items/resolve', { params: { barcode: code } });
      const unit = data.itemUnitId ? data.item.units.find((u) => u.id === data.itemUnitId) : undefined;
      addComponent(data.item, unit ? unit.factor : '1');
    } catch {
      toast.error(t('boms.unknownBarcode', { defaultValue: 'No product found for that barcode.' }));
    }
  };

  const productOptions = useMemo<ComboboxOption[]>(
    () => productSearch.items.map((it) => ({
      value: it.id,
      label: `${it.code} — ${it.name}`,
      sublabel: it.barcode ?? undefined,
      keywords: `${it.code} ${it.name} ${it.barcode ?? ''} ${productSearch.q}`,
    })),
    [productSearch],
  );
  const scanOptions = useMemo<ComboboxOption[]>(
    () => search.items.map((it) => ({
      value: it.id,
      label: `${it.code} — ${it.name}`,
      sublabel: it.barcode ?? undefined,
      keywords: `${it.code} ${it.name} ${it.barcode ?? ''} ${search.q}`,
    })),
    [search],
  );
  const warehouseOptions = useMemo<ComboboxOption[]>(
    () => (warehouses ?? []).filter((w) => w.isActive || w.id === warehouseId).map((w) => ({ value: w.id, label: w.name })),
    [warehouses, warehouseId],
  );

  if (!warehouses || !hydrated) return <Card><Skeleton className="h-40 w-full" /></Card>;

  const usable = lines.filter((l) => safeDecimal(l.quantity).gt(0));
  const canSubmit = !!product && !!warehouseId && safeDecimal(qty).gt(0) && usable.length > 0 && !saving;
  const assembling = kind === 'assemble';

  // "Can make up to N": min over BOM-derived lines of on-hand ÷ per-output-unit.
  // Manual lines (perUnit null) are skipped — they carry no per-unit ratio.
  const maxProducible = ((): string | null => {
    if (!assembling || !warehouseId) return null;
    const ratios = lines.filter((l) => l.perUnit && safeDecimal(l.perUnit).gt(0));
    if (ratios.length === 0) return null;
    let min: ReturnType<typeof safeDecimal> | null = null;
    for (const l of ratios) {
      const lv = levels[`${warehouseId}:${l.itemId}`];
      if (!lv) return null; // still loading
      const can = safeDecimal(lv.quantity).dividedBy(safeDecimal(l.perUnit!));
      if (min === null || can.lt(min)) min = can;
    }
    return min === null ? null : min.toDecimalPlaces(0, 1).toString(); // floor
  })();

  // Estimated component cost, PER CURRENCY (no client FX — the server converts
  // at post with the day's rate; mixing currencies here would fabricate a number).
  const costPreview = ((): Array<{ ccy: string; total: string }> => {
    if (!warehouseId) return [];
    const byCcy = new Map<string, ReturnType<typeof safeDecimal>>();
    for (const l of usable) {
      const lv = levels[`${warehouseId}:${l.itemId}`];
      if (!lv || !lv.ccy || safeDecimal(lv.avgCost).lte(0)) continue;
      const total = safeDecimal(l.quantity).times(safeDecimal(lv.avgCost));
      byCcy.set(lv.ccy, (byCcy.get(lv.ccy) ?? safeDecimal(0)).plus(total));
    }
    if (assembling && safeDecimal(normalizeNumericInput(extraCost) || '0').gt(0) && extraCcy) {
      byCcy.set(extraCcy, (byCcy.get(extraCcy) ?? safeDecimal(0)).plus(safeDecimal(normalizeNumericInput(extraCost))));
    }
    return [...byCcy.entries()].map(([ccy, total]) => ({ ccy, total: total.toDecimalPlaces(4).toString() }));
  })();

  const buildBody = (): ProductionOrderCreate => ({
    kind,
    itemId: product!.id,
    warehouseId,
    bomId: bom && bom !== 'none' ? bom.id : null,
    unitFactor: '1',
    quantity: safeDecimal(normalizeNumericInput(qty)).toDecimalPlaces(4).toString(),
    extraCost: assembling ? safeDecimal(normalizeNumericInput(extraCost) || '0').toDecimalPlaces(4).toString() : '0',
    extraCostCurrencyCode: assembling && safeDecimal(normalizeNumericInput(extraCost) || '0').gt(0) ? (extraCcy || null) : null,
    notes: notes.trim() || null,
    lines: usable.map((l) => ({
      itemId: l.itemId,
      unitFactor: '1',
      quantity: safeDecimal(l.quantity).toDecimalPlaces(4).toString(),
    })),
  });

  const postErrorToast = (err: unknown): void => {
    const cause = (err as { response?: { data?: { cause?: { code?: string; itemName?: string; available?: string; requested?: string; from?: string; to?: string } } } })
      .response?.data?.cause;
    if (cause?.code === 'stock.insufficient') {
      toast.error(t('orders.err.insufficient', {
        item: cause.itemName ?? '—', available: trimMoney(cause.available ?? '0'), requested: trimMoney(cause.requested ?? '0'),
        defaultValue: 'Not enough "{{item}}" on hand — {{available}} available, {{requested}} needed.',
      }));
    } else if (cause?.code === 'production_cost_no_rate') {
      toast.error(t('orders.err.noRate', {
        from: cause.from ?? '—', to: cause.to ?? '—',
        defaultValue: 'No exchange rate between {{from}} and {{to}} — add one in Settings → Currencies.',
      }));
    } else if (cause?.code === 'production_cost_currency_required') {
      toast.error(t('orders.err.currencyRequired', { defaultValue: 'Components are costed in different currencies — pick the cost currency first.' }));
    } else {
      toast.error(t('orders.err.postFailed', { defaultValue: 'Posting failed.' }));
    }
  };

  const submit = async (andPost: boolean): Promise<void> => {
    if (!canSubmit) return;
    if (andPost) {
      const ok = await confirmDialog({
        title: assembling
          ? t('orders.postTitle', { defaultValue: 'Make it now?' })
          : t('orders.postTitleDisassemble', { defaultValue: 'Take it apart now?' }),
        message: assembling
          ? t('orders.postConfirm', {
              n: usable.length, qty: trimMoney(qty), name: product?.name ?? '',
              defaultValue: 'Consumes {{n}} component line(s) and adds {{qty}} × {{name}} to stock at the computed cost.',
            })
          : t('orders.postConfirmDisassemble', {
              qty: trimMoney(qty), name: product?.name ?? '',
              defaultValue: 'Consumes {{qty}} × {{name}} and returns its components to stock.',
            }),
        confirmLabel: t('orders.post', { defaultValue: 'Post' }),
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const r = await api().post<ProductionOrder>('/manufacturing/orders', buildBody());
      if (andPost) {
        try {
          await api().post(`/manufacturing/orders/${r.data.id}/post`, costCcy ? { costCurrencyCode: costCcy } : {});
          toast.success(t('orders.done.post', { doc: r.data.docNumber, defaultValue: '{{doc}} posted — stock updated.' }));
        } catch (err) {
          postErrorToast(err);
        }
      }
      navigate(`/manufacturing/orders/${r.data.id}`);
    } catch {
      toast.error(t('orders.err.createFailed', { defaultValue: 'Could not create the order.' }));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (): Promise<void> => {
    if (!canSubmit || !id) return;
    setSaving(true);
    try {
      await api().patch(`/manufacturing/orders/${id}`, buildBody());
      navigate(`/manufacturing/orders/${id}`);
    } catch {
      toast.error(t('orders.err.updateFailed', { defaultValue: 'Could not save the order.' }));
    } finally {
      setSaving(false);
    }
  };

  const productLevel = product && warehouseId ? levels[`${warehouseId}:${product.id}`] : undefined;

  return (
    <div className="flex flex-col gap-4">
      <Link to="/manufacturing/orders" className="inline-flex w-fit items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden />
        {t('orders.title', { defaultValue: 'Production orders' })}
      </Link>

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Factory className="h-6 w-6 text-brand" aria-hidden />
          {edit ? t('orders.editTitle', { defaultValue: 'Edit production order' }) : t('orders.new', { defaultValue: 'New production order' })}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('orders.newHint', { defaultValue: 'Pick a product — its recipe fills in the components. Nothing moves until the order is posted.' })}
        </p>
      </header>

      <Card>
        <div className="mb-4 inline-flex items-center rounded-full border border-border p-0.5">
          {(['assemble', 'disassemble'] as const).map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              onClick={() => setKind(k)}
              className={cn(
                'inline-flex h-8 items-center justify-center gap-1.5 rounded-full px-4 text-xs font-semibold transition',
                kind === k ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg',
              )}
            >
              {k === 'assemble' ? <Hammer className="h-3.5 w-3.5" aria-hidden /> : <Undo2 className="h-3.5 w-3.5" aria-hidden />}
              {t(`orders.kinds.${k}`, { defaultValue: k === 'assemble' ? 'Make (assemble)' : 'Take apart (disassemble)' })}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField id="po-product" label={t('orders.form.pickProduct', { defaultValue: 'Product' })} required>
            {product ? (
              <div className="flex h-10 items-center justify-between gap-2 rounded-md border border-border bg-bg-subtle px-3 text-sm">
                <span className="truncate"><span className="font-mono text-xs text-fg-muted">{product.code}</span> · {product.name}</span>
                {!edit && (
                  <button type="button" className="text-xs font-semibold text-brand" onClick={() => { setProduct(null); setBom(null); setLines([]); }}>
                    {t('orders.form.change', { defaultValue: 'Change' })}
                  </button>
                )}
              </div>
            ) : (
              <div ref={productWrapRef} onInput={(e) => setProductTerm((e.target as HTMLInputElement).value)}>
                <Combobox
                  id="po-product"
                  options={productOptions}
                  onChange={(v) => void pickProduct(v)}
                  clearOnSelect
                  autoFocus={!edit}
                  placeholder={t('orders.form.productPlaceholder', { defaultValue: 'Search by name, code or barcode…' })}
                  ariaLabel={t('orders.form.pickProduct', { defaultValue: 'Product' })}
                />
              </div>
            )}
          </FormField>
          <FormField id="po-wh" label={t('orders.form.warehouse', { defaultValue: 'Warehouse' })} required>
            <Combobox
              id="po-wh"
              options={warehouseOptions}
              value={warehouseId || undefined}
              onChange={(v) => setWarehouseId(v ?? '')}
              ariaLabel={t('orders.form.warehouse', { defaultValue: 'Warehouse' })}
            />
          </FormField>
          <FormField
            id="po-qty"
            label={assembling ? t('orders.form.outputQty', { defaultValue: 'Quantity to make' }) : t('orders.form.inputQty', { defaultValue: 'Quantity to take apart' })}
            required
            helperText={!assembling && productLevel ? t('orders.form.onHand', { n: trimMoney(productLevel.quantity), defaultValue: '{{n}} on hand' }) : undefined}
          >
            <NumberInput id="po-qty" className="font-mono" value={qty} onValueChange={changeQty} />
          </FormField>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField id="po-ccy" label={t('orders.form.currency', { defaultValue: 'Cost currency' })} helperText={t('orders.form.currencyHint', { defaultValue: 'Leave empty to decide automatically' })}>
            <CurrencySelect id="po-ccy" value={costCcy} onChange={setCostCcy} includeEmpty emptyLabel={t('orders.form.auto', { defaultValue: 'Automatic' })} />
          </FormField>
          {assembling && (
            <>
              <FormField id="po-extra" label={t('boms.extraCost', { defaultValue: 'Extra cost per run' })} helperText={t('boms.extraCostHint', { defaultValue: 'Labor, electricity, packaging — added to the finished cost' })}>
                <NumberInput id="po-extra" className="font-mono" value={extraCost} onValueChange={(v) => setExtraCost(normalizeNumericInput(v))} />
              </FormField>
              <FormField id="po-extra-ccy" label={t('boms.currency', { defaultValue: 'Currency' })}>
                <CurrencySelect id="po-extra-ccy" value={extraCcy} onChange={setExtraCcy} includeEmpty emptyLabel="—" />
              </FormField>
            </>
          )}
          <FormField id="po-notes" label={t('orders.notes', { defaultValue: 'Notes' })}>
            <Input id="po-notes" value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
          </FormField>
        </div>
      </Card>

      {product && bom === 'none' && (
        <Card className="border-warning/50 p-4 text-sm">
          {t('orders.form.noRecipe', { defaultValue: 'This product has no recipe yet — add components below by hand, or save its recipe first on the product’s Recipe tab.' })}{' '}
          <Link to={`/inventory/items/${product.id}/edit?tab=recipe`} className="font-semibold text-brand">
            {t('orders.form.goToRecipe', { defaultValue: 'Open the Recipe tab' })}
          </Link>
        </Card>
      )}

      {assembling && maxProducible !== null && (
        <Card className="flex flex-wrap items-center justify-between gap-2 p-4 text-sm">
          <span>
            {t('orders.form.maxProducible', { n: maxProducible, defaultValue: 'With what is on hand you can make up to {{n}}.' })}
          </span>
          {safeDecimal(maxProducible).gt(0) && (
            <Button type="button" variant="secondary" size="sm" onClick={() => changeQty(maxProducible)}>
              {t('orders.form.useMax', { defaultValue: 'Use max' })}
            </Button>
          )}
        </Card>
      )}

      <div
        ref={scanWrapRef}
        onInput={(e) => setTerm((e.target as HTMLInputElement).value)}
        className="flex items-center gap-2 rounded-lg border border-border bg-bg-subtle px-3 py-2"
      >
        <ScanLine className="h-5 w-5 flex-none text-fg-muted" aria-hidden />
        <div className="flex-1">
          <Combobox
            options={scanOptions}
            onChange={onScanPick}
            onEnterUnmatched={(code) => void resolveScan(code)}
            clearOnSelect
            placeholder={t('boms.scanAdd', { defaultValue: 'Scan a barcode or search to add a component…' })}
            ariaLabel={t('boms.scanAdd', { defaultValue: 'Scan a barcode or search to add a component…' })}
          />
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-bg-muted text-fg-subtle">
            <tr>
              <th className="w-10 px-3 py-2 text-start font-medium">#</th>
              <th className="px-3 py-2 text-start font-medium">
                {assembling ? t('orders.form.component', { defaultValue: 'Component (consumed)' }) : t('orders.form.componentBack', { defaultValue: 'Component (returned)' })}
              </th>
              <th className="w-28 px-3 py-2 text-end font-medium">{t('orders.form.onHandCol', { defaultValue: 'On hand' })}</th>
              <th className="w-32 px-3 py-2 text-end font-medium">{t('orders.form.avgCost', { defaultValue: 'Avg cost' })}</th>
              <th className="w-32 px-3 py-2 text-end font-medium">{t('orders.form.total', { defaultValue: 'Qty' })}</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-fg-muted">
                  {t('orders.form.emptyGrid', { defaultValue: 'Pick a product with a recipe, or add components by scan/search above.' })}
                </td>
              </tr>
            ) : lines.map((l, idx) => {
              const lv = warehouseId ? levels[`${warehouseId}:${l.itemId}`] : undefined;
              const over = assembling && lv !== undefined && safeDecimal(l.quantity).gt(safeDecimal(lv.quantity));
              return (
                <tr key={l._key} className="border-t border-border">
                  <td className="px-3 py-2 text-fg-muted">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{l.name}</div>
                    <div className="font-mono text-xs text-fg-muted">{l.code} · {l.uom}</div>
                  </td>
                  <td className="px-3 py-2 text-end font-mono text-fg-muted">{lv ? trimMoney(lv.quantity) : '—'}</td>
                  <td className="px-3 py-2 text-end font-mono text-fg-muted">
                    {lv && lv.ccy && safeDecimal(lv.avgCost).gt(0)
                      ? formatMoney(lv.avgCost, { locale: i18n.language, currency: lv.ccy })
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <NumberInput
                      className={cn('text-end font-mono', over && 'text-warning')}
                      value={l.quantity}
                      onValueChange={(raw) => {
                        const clean = normalizeNumericInput(raw);
                        setLines((prev) => prev.map((x) => (x._key === l._key ? { ...x, quantity: clean, perUnit: null } : x)));
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusScan(); } }}
                      title={over ? t('orders.form.overOnHand', { defaultValue: 'More than is on hand — posting will be refused.' }) : undefined}
                      aria-label={t('orders.form.total', { defaultValue: 'Qty' })}
                    />
                  </td>
                  <td className="px-3 py-2 text-end">
                    <Button type="button" variant="ghost" size="sm" aria-label={t('boms.removeLine', { defaultValue: 'Remove component' })} onClick={() => setLines((prev) => prev.filter((x) => x._key !== l._key))}>
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {costPreview.length > 0 && (
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="text-fg-muted">
                {assembling
                  ? t('orders.preview.title', { defaultValue: 'Estimated cost' })
                  : t('orders.preview.titleBack', { defaultValue: 'Estimated value returned' })}:
              </span>
              {costPreview.map((row) => (
                <span key={row.ccy} className="rounded-full border border-border bg-bg-subtle px-2.5 py-0.5 font-mono text-xs">
                  {formatMoney(row.total, { locale: i18n.language, currency: row.ccy })}
                </span>
              ))}
              <span className="text-xs text-fg-muted">{t('orders.preview.estimateHint', { defaultValue: '(estimate — the exact cost is computed when posting, at the day’s rate)' })}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="secondary" disabled={saving} onClick={() => navigate(edit && id ? `/manufacturing/orders/${id}` : '/manufacturing/orders')}>
            {t('orders.form.cancel', { defaultValue: 'Cancel' })}
          </Button>
          {edit ? (
            <Button type="button" disabled={!canSubmit} loading={saving} onClick={() => void saveEdit()}>
              {t('orders.form.save', { defaultValue: 'Save changes' })}
            </Button>
          ) : (
            <>
              <Button type="button" variant="secondary" disabled={!canSubmit} loading={saving} onClick={() => void submit(false)}>
                {t('orders.form.create', { defaultValue: 'Save draft' })}
              </Button>
              {canPost && (
                <Button type="button" disabled={!canSubmit} loading={saving} onClick={() => void submit(true)}>
                  {t('orders.form.saveAndPost', { defaultValue: 'Save & post' })}
                </Button>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
